import {
  differenceInCalendarDays,
  eachMonthOfInterval,
  endOfMonth,
  getDaysInMonth,
  max,
  min,
  startOfDay,
  startOfMonth,
} from 'date-fns'
import { downloadLicenseBillingPdf } from 'src/api/customer-license'
import {
  LICENSE_BILLING_CURRENCY_CODE,
  LICENSE_BILLING_CURRENCY_LOCALE,
  LICENSE_SYSTEM_DEFAULT_LOCALITY_MONTHLY,
  LICENSE_SYSTEM_DEFAULT_SERVICE_STATION_MONTHLY,
  LICENSE_SYSTEM_DEFAULT_STORAGE_MONTHLY,
} from 'src/constant'
import type {
  BillingAsset,
  BillingAssetRowAction,
  BillingAssetType,
  BillingCustomer,
  CustomerBillingTab,
  ExportBillingPdfParams,
  ICustomerLicenseFacilityRow,
  ICustomerLicenseUsage,
} from 'src/generated-types/customer-license'

export const formatBillingCurrency = (
  amount: number,
  locale = LICENSE_BILLING_CURRENCY_LOCALE,
  currency = LICENSE_BILLING_CURRENCY_CODE,
) => {
  const digits = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(amount)
  return `${currency} ${digits}`
}

/**
 * Prorates a monthly license rate across each calendar month that overlaps [from, to]
 * (inclusive calendar days in local time).
 */
export const billingCostForDateRange = (monthlyRate: number, from: Date | null, to: Date | null): number => {
  if (!from || !to || from > to) return 0
  const fromDay = startOfDay(from)
  const toDay = startOfDay(to)
  let total = 0
  for (const monthStart of eachMonthOfInterval({ start: fromDay, end: toDay })) {
    const monthFirst = startOfMonth(monthStart)
    const monthLast = startOfDay(endOfMonth(monthStart))
    const overlapStart = max([fromDay, monthFirst])
    const overlapEnd = min([toDay, monthLast])
    if (overlapStart > overlapEnd) continue
    const daysInMonth = getDaysInMonth(monthStart)
    const overlapDays = differenceInCalendarDays(overlapEnd, overlapStart) + 1
    total += monthlyRate * (overlapDays / daysInMonth)
  }
  return total
}

/**
 * Sidebar total for the selected period: prorated monthly unit cost × facility counts per type.
 * Global service station providers only bill service stations (matches license-rates rules).
 */
export const billingCustomerSummaryPeriodCost = (
  customer: BillingCustomer,
  from: Date | null,
  to: Date | null,
): number => {
  if (!from || !to || from > to) return 0
  if (customer.isGlobalServiceStationProvider) {
    return (
      billingCostForDateRange(customer.serviceStationUnitMonthly, from, to) * customer.serviceStationsCount
    )
  }
  return (
    billingCostForDateRange(customer.localityUnitMonthly, from, to) * customer.localitiesCount +
    billingCostForDateRange(customer.serviceStationUnitMonthly, from, to) * customer.serviceStationsCount +
    billingCostForDateRange(customer.storageUnitMonthly, from, to) * customer.storagesCount
  )
}

const normalizeSearch = (q: string) => q.trim().toLowerCase()

export const filterBillingCustomers = (
  customers: BillingCustomer[],
  tab: CustomerBillingTab,
  search: string,
): BillingCustomer[] => {
  const byTab = customers.filter(c => (tab === 'active' ? c.isActive : !c.isActive))
  const q = normalizeSearch(search)
  if (!q) return byTab
  return byTab.filter(c => c.name.toLowerCase().includes(q))
}

export const filterBillingAssets = (
  assets: BillingAsset[],
  customerId: string | null,
  search: string,
  assetType: BillingAssetType | 'all',
): BillingAsset[] => {
  let list = assets
  if (customerId) {
    list = list.filter(a => a.customerId === customerId)
  }
  if (assetType !== 'all') {
    list = list.filter(a => a.type === assetType)
  }
  const q = normalizeSearch(search)
  if (!q) return list
  return list.filter(
    a =>
      a.assetName.toLowerCase().includes(q) ||
      a.customerName.toLowerCase().includes(q) ||
      a.type.toLowerCase().includes(q),
  )
}

export const exportBillingPdfReport = async (params: ExportBillingPdfParams): Promise<void> => {
  const customerIds = params.customerIds.map(id => Number(id)).filter(n => !Number.isNaN(n))
  await downloadLicenseBillingPdf({
    from: params.from.toISOString(),
    to: params.to.toISOString(),
    isActive: params.isActive,
    ...(params.search.trim() ? { search: params.search.trim() } : {}),
    customerIds,
  })
}

export const defaultFirstCustomerId = (customers: BillingCustomer[]): string | null =>
  customers.length > 0 ? customers[0].id : null

/** Wire to customer edit dialog / navigation when API exists */
export const handleEditBillingCustomer = (customerId: string): void => {
  void customerId
}

export const handleAssetBillingRowAction = (assetId: string, action: BillingAssetRowAction): void => {
  void assetId
  void action
}

export const mapLicenseUsageToBillingCustomers = (items: ICustomerLicenseUsage[]): BillingCustomer[] =>
  items.map(u => ({
    id: String(u.id),
    name: u.name,
    totalAmount: Number(u.monthlyTotal ?? 0),
    rateReference: Math.round(Number(u.referenceRate ?? 0)),
    isActive: u.isActive !== false,
    isGlobalServiceStationProvider: u.isGlobalServiceStationProvider === true,
    localitiesCount: u.localitiesCount ?? 0,
    serviceStationsCount: u.serviceStationsCount ?? 0,
    storagesCount: u.storagesCount ?? 0,
    localityUnitMonthly: Number(u.referenceRate ?? LICENSE_SYSTEM_DEFAULT_LOCALITY_MONTHLY),
    serviceStationUnitMonthly: Number(
      u.serviceStationUnitMonthlyRate ?? LICENSE_SYSTEM_DEFAULT_SERVICE_STATION_MONTHLY,
    ),
    storageUnitMonthly: Number(u.storageUnitMonthlyRate ?? LICENSE_SYSTEM_DEFAULT_STORAGE_MONTHLY),
  }))

const facilityTypeToBilling = (facilityType: string): BillingAsset['type'] => {
  if (facilityType === 'serviceStation') return 'serviceStation'
  if (facilityType === 'storage') return 'storage'
  return 'locality'
}

export const mapFacilitiesToBillingAssets = (
  rows: ICustomerLicenseFacilityRow[],
  customerId: string,
  customerName: string,
): BillingAsset[] =>
  rows.map(row => ({
    id: `${row.facilityType}-${row.id}`,
    entityId: row.id,
    customerId,
    customerName,
    assetName: row.name,
    type: facilityTypeToBilling(row.facilityType),
    status: row.isActive ? 'active' : 'inactive',
    monthlyRate: Number(row.monthlyRate ?? 0),
  }))
